import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    makeStyles,
} from '@material-ui/core/';
import * as React from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { KitTypeCategory } from '../actions/getKitTypeCategories';
import type { KitType } from '../actions/getKitTypes';
import { sendRequest } from '../api/websocketApi';
import { ReactComponent as CatalogueImage } from '../assets/catalogue.svg';
import { handleDownload } from '../helpers/handleDownload';
import { ReactComponent as ExpandMoreIcon } from '../icons/expandMore.svg';
import useStore from '../store/store';

const TestMenuPage = () => {
    const setTitle = useStore((state) => state.setTitle);
    const classes = useStyles({});
    const kitTypes = useStore((state) => state.kitTypes);
    const [kitTypeCategories, setKitTypeCategories] = React.useState<
        KitTypeCategory[]
    >([]);
    const setFormError = useStore((state) => state.setFormError);
    const navigate = useNavigate();

    useEffect(() => {
        setTitle('Genome Portal - Test Menu');
    }, [setTitle]);

    useEffect(() => {
        interface APIResponse {
            success: boolean;
            kitTypeCategories?: KitTypeCategory[];
            errCode?: 'invalid_token' | 'auth_failed' | 'access_denied';
            errDetails?: string;
        }
        sendRequest(
            {
                endpoint: '/kit-type-category/get-all',
            },
            (response: APIResponse) => {
                const { success, kitTypeCategories, errCode, errDetails } =
                    response;
                if (success && kitTypeCategories) {
                    kitTypeCategories.sort(
                        (a, b) => a.KitTypeCategoryId - b.KitTypeCategoryId
                    );
                    setKitTypeCategories(kitTypeCategories);
                } else if (errCode) {
                    console.error(errCode);
                    setFormError(errCode);
                    setTimeout(() => setFormError(errCode), 0);
                    if (errDetails) console.error(errDetails);
                }
            }
        );
    }, [setFormError]);

    return (
        <>
            <div className={classes.root}>
                <div className={classes.header}>
                    <CatalogueImage className={classes.catalogueImage} />
                    <div className={classes.headerContent}>
                        <div className={classes.headerText}>
                            <h1 className={classes.headerMainText}>
                                Test Menu
                            </h1>
                            <p className={classes.headerSecondaryText}>
                                {`Welcome to the Genome Test Catalogue. Below
                            you'll find all available genomic testing options
                            currently offered through our laboratory services.
                            If you require additional information or have
                            specific testing needs not listed here, please
                            contact our genomics team.`}
                            </p>
                        </div>
                        <Button
                            className={classes.contactUsButton}
                            onClick={() => navigate('/contact')}
                        >
                            Contact Us
                        </Button>
                    </div>
                </div>
                <h2 className={classes.subheader}>Available Tests:</h2>
                <div className={classes.catalogue}>
                    <div className={classes.catalogueContent}>
                        {kitTypeCategories.map((category, index) => (
                            <div key={category.KitTypeCategoryId}>
                                <div className={classes.categoryHeader}>
                                    <div className={classes.columnType}>
                                        {category.Name}
                                    </div>
                                    {index === 0 && (
                                        <>
                                            <div
                                                className={classes.columnHeader}
                                            >
                                                ID
                                            </div>
                                            <div
                                                className={classes.columnHeader}
                                            >
                                                Turnaround Time
                                            </div>
                                            <div
                                                className={
                                                    classes.lastColumnHeader
                                                }
                                            >
                                                Last update
                                            </div>
                                        </>
                                    )}
                                </div>
                                {kitTypes
                                    .filter(
                                        (kitType: KitType) =>
                                            kitType.KitTypeCategoryId ===
                                            category.KitTypeCategoryId
                                    )
                                    .map((kitType: KitType) => (
                                        <Accordion
                                            key={kitType.KitTypeId}
                                            className={classes.accordion}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                            >
                                                <div
                                                    className={
                                                        classes.accordionRow
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            classes.columnType
                                                        }
                                                    >
                                                        {kitType.Type}
                                                    </div>
                                                    <div
                                                        className={
                                                            classes.column
                                                        }
                                                    >
                                                        {kitType.KitTypeCatalogueId ??
                                                            'N/A'}
                                                    </div>
                                                    <div
                                                        className={
                                                            classes.column
                                                        }
                                                    >
                                                        {kitType.TurnaroundTime}
                                                    </div>
                                                    <div
                                                        className={
                                                            classes.lastColumn
                                                        }
                                                    >
                                                        {new Date(
                                                            kitType.UpdateDate
                                                        ).toLocaleDateString()}
                                                    </div>
                                                </div>
                                            </AccordionSummary>
                                            <AccordionDetails
                                                className={classes.details}
                                            >
                                                <table
                                                    className={
                                                        classes.detailsTable
                                                    }
                                                >
                                                    <tr>
                                                        <th
                                                            className={
                                                                classes.tableHeaderCell
                                                            }
                                                            align="left"
                                                        >
                                                            Test Description
                                                        </th>
                                                        <th
                                                            className={
                                                                classes.tableHeaderCell
                                                            }
                                                            align="left"
                                                        >
                                                            Sample Type Required
                                                        </th>
                                                        <th
                                                            className={
                                                                classes.tableHeaderCell
                                                            }
                                                            align="left"
                                                        >
                                                            Transportation
                                                            Temperature
                                                        </th>
                                                        <th
                                                            className={
                                                                classes.tableHeaderCell
                                                            }
                                                            align="left"
                                                        >
                                                            Reports Provided
                                                        </th>
                                                        <th
                                                            className={
                                                                classes.tableHeaderCell
                                                            }
                                                            align="center"
                                                        >
                                                            TRF
                                                        </th>
                                                    </tr>
                                                    <tr>
                                                        <td
                                                            className={
                                                                classes.tableCell
                                                            }
                                                            align="left"
                                                        >
                                                            {
                                                                kitType.Description
                                                            }
                                                        </td>
                                                        <td
                                                            className={
                                                                classes.tableCell
                                                            }
                                                            align="left"
                                                        >
                                                            {
                                                                kitType.RequiredSample
                                                            }
                                                        </td>
                                                        <td
                                                            className={
                                                                classes.tableCell
                                                            }
                                                            align="left"
                                                        >
                                                            {
                                                                kitType.TransportTempo
                                                            }
                                                        </td>
                                                        <td
                                                            className={
                                                                classes.tableCell
                                                            }
                                                            align="left"
                                                        >
                                                            {
                                                                kitType.ProvidedReports
                                                            }
                                                        </td>
                                                        <td
                                                            className={
                                                                classes.tableCell
                                                            }
                                                            align="center"
                                                        >
                                                            <button
                                                                className={
                                                                    classes.downloadTrf
                                                                }
                                                                onClick={() =>
                                                                    handleDownload(
                                                                        kitType.TrfFilePath ??
                                                                            '',
                                                                        false,
                                                                        true
                                                                    )
                                                                }
                                                            >
                                                                <p
                                                                    className={
                                                                        classes.downloadTrf
                                                                    }
                                                                >
                                                                    {kitType.TrfFilePath
                                                                        ? 'download'
                                                                        : ''}
                                                                </p>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                </table>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        padding: '40px',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '32px',
        flex: '1 0 0',
        alignSelf: 'stretch',
        background: theme.palette.primary.lighter,
    },
    header: {
        display: 'flex',
        width: '100%',
        padding: '21px 58px',
        alignItems: 'center',
        gap: '80px',
        justifyContent: 'space-between',
        borderRadius: '20px',
        background: '#FFF',
    },
    catalogueImage: {
        width: '493px',
        height: '280px',
        flexShrink: 0,
    },
    headerContent: {
        display: 'flex',
        flex: 1,
        height: 'auto',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
        gap: '28px',
    },
    headerText: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
    },
    headerMainText: {
        color: theme.palette.primary.main,
        fontFamily: 'Poppins',
        fontSize: '32px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '130%',
        letterSpacing: '-0.64px',
    },
    headerSecondaryText: {
        alignSelf: 'stretch',
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%',
    },
    contactUsButton: {
        display: 'flex',
        width: '131px',
        padding: '6px 12px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        borderRadius: '24px',
        backgroundColor: theme.palette.primary.light,
        textTransform: 'none',
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
        letterSpacing: '0.09px',
    },
    subheader: {
        color: theme.palette.primary.main,
        fontFamily: 'Poppins',
        fontSize: '24px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '140%',
        letterSpacing: '0.12px',
        alignSelf: 'flex-start',
    },
    catalogue: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderRadius: '12px',
        width: '100%',
        background: theme.palette.primary.background,
    },
    accordion: {
        width: '100%',
        height: 'auto',
    },
    categoryHeader: {
        display: 'flex',
        height: '56px',
        padding: '16px 24px 20px 32px',
        justifyContent: 'space-between',
        alignItems: 'center',
        flex: '1 0 0',
        background: theme.palette.primary.light,
        width: '100%',
    },
    icon: {
        width: '29px',
        height: '29px',
        flexShrink: 0,
        aspectTatio: '1/1',
    },
    catalogueContent: {
        width: '100%',
    },
    accordionRow: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        padding: '16px 24px',
        boxSizing: 'border-box',
        columnGap: '16px',
    },
    columnHeader: {
        flex: '1 1 0',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        textAlign: 'center',
    },
    column: {
        flex: '1 1 0',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        textAlign: 'center',
    },
    lastColumnHeader: {
        flex: '1 1 0',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        textAlign: 'right',
        marginRight: '35px',
    },
    lastColumn: {
        flex: '1 1 0',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        textAlign: 'right',
    },
    columnType: {
        flex: '2 1 0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    details: {
        backgroundColor: theme.palette.primary.background,
        display: 'flex',
        minHeight: '48px',
        padding: '32px 32px 32px 50px',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        alignContent: 'space-between',
        alignSelf: 'stretch',
        flexWrap: 'wrap',
    },
    detailsTable: {
        width: '100%',
    },
    tableHeaderCell: {
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingBottom: '10px',
    },
    tableCell: {
        paddingLeft: '10px',
        paddingRight: '10px',
    },
    downloadTrf: {
        color: theme.palette.gray.dark,
        fontFamily: 'Noto Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '150%',
        letterSpacing: '-0.252px',
        textDecorationLine: 'underline',
        textDecorationStyle: 'solid',
        textDecorationSkipInk: 'none',
        textDecorationThickness: 'auto',
        textUnderlineOffset: 'auto',
        textUnderlinePosition: 'from-font',
        cursor: 'pointer',
        border: 'none',
        background: 'none',
    },
}));

export default TestMenuPage;
